import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useQuery } from "react-query";
import { trackEvent } from "../../../shared/analytics";
import { getLatestReadingOrders } from "../../../services/readingOrdersApi";

interface ReadingOrder {
  id: number;
  title: string;
  description: string;
  thumbnail: string;
  comicsCount: number;
  franchise: number;
  fromYear: number | null;
  isCommunity: number;
  isOngoing: number;
  order: number;
  premiumOnly: number;
  privacyMode: number;
  published: number;
  slug: string;
  readingCount: number | null;
  toYear: number | null;
  type: number;
  updatedAt: string;
  years: number | null;
  isPremium?: boolean;
}

interface ReadingOrderCardProps {
  order: ReadingOrder;
  isDuplicate?: boolean;
}

const ReadingOrderCard = ({
  order,
  isDuplicate = false,
}: ReadingOrderCardProps) => (
  <Box
    className="has-animation latest-reading-order-card"
    data-reading-order-card
    minW="350px"
  >
    <Box
      className="latest-reading-order-card-media"
      overflow="hidden"
      bg="gray.700"
      position="relative"
      borderRadius="lg"
    >
      <Box
        h="100%"
        w="100%"
        bgGradient="linear(to-t, black, transparent)"
        position="absolute"
        top="0"
        left="0"
      >
        <Box w="100%" position="absolute" bottom="0">
          <Flex>
            <Box pt="3" px="6" textAlign="left">
              <Text as="h4" fontWeight="bold" fontSize="xl">
                {order.title}
              </Text>
            </Box>
            <Spacer />
          </Flex>
          <Flex
            px="6"
            pb="4"
            pt="0"
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex alignItems="center" gap={3} minW={0}>
              <Text fontSize="sm" whiteSpace="nowrap">
                {!!order.years && `${order.years} • `}
                {order.comicsCount} comics
              </Text>
              <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">
                Added on{" "}
                {new Date(order.updatedAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                })}
              </Text>
            </Flex>
            <a
              href={`/comic-reading-order/${order.slug}`}
              tabIndex={isDuplicate ? -1 : undefined}
            >
              <Button
                variant="outline"
                size="sm"
                colorScheme="red"
                tabIndex={isDuplicate ? -1 : undefined}
              >
                Read
              </Button>
            </a>
          </Flex>
        </Box>
      </Box>
      <Image
        className="latest-reading-order-card-image"
        height="200px"
        width="100%"
        objectFit="cover"
        src={order.thumbnail}
        alt={order.title}
      />
    </Box>
  </Box>
);

const LatestReadingOrders = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftGradient(scrollLeft > 0);
      setShowRightGradient(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["latest-reading-orders"],
    queryFn: () => {
      return getLatestReadingOrders();
    },
  });

  if (isLoading) return <div></div>;
  if (error) return <div>Error loading reading orders</div>;

  const readingOrders = data?.data ?? [];

  return (
    <section className="features section latest-reading-orders-section">
      <Container maxW="container.xl" pt={24}>
        <Heading as="h2" mb={8} textAlign={"center"}>
          Latest Reading Orders
        </Heading>
      </Container>
      <Box className="latest-reading-orders-track" position="relative">
        <Box position="relative">
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            width="20px"
            bgGradient="linear(to-r, black, transparent)"
            bgColor="rgba(0, 0, 0, 0.3)"
            zIndex={1}
            opacity={showLeftGradient ? 0.5 : 0}
            transition="opacity 0.2s"
          />
          <Box
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            width="20px"
            bgGradient="linear(to-l, black, transparent)"
            bgColor="rgba(0, 0, 0, 0.3)"
            zIndex={1}
            opacity={showRightGradient ? 0.5 : 0}
            transition="opacity 0.2s"
          />
          <Flex
            ref={scrollRef}
            onScroll={handleScroll}
            className="latest-reading-orders-marquee"
            role="region"
            aria-label="Latest reading orders carousel"
            tabIndex={0}
            flexDirection={"row"}
            px={{ base: 4, md: 8 }}
            overflowX="auto"
            maxW="100%"
            css={{
              "&::-webkit-scrollbar": {
                height: "0px",
              },
              "&::-webkit-scrollbar-track": {
                background: "gray.100",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "gray.400",
                borderRadius: "4px",
                "&:hover": {
                  background: "gray.500",
                },
              },
            }}
          >
            <Flex className="latest-reading-orders-marquee-track">
              <Flex className="latest-reading-orders-marquee-group" gap={8}>
                {readingOrders.map((order: ReadingOrder) => (
                  <ReadingOrderCard key={order.id} order={order} />
                ))}
              </Flex>
              <Flex
                className="latest-reading-orders-marquee-group"
                gap={8}
                aria-hidden="true"
              >
                {readingOrders.map((order: ReadingOrder) => (
                  <ReadingOrderCard
                    key={`duplicate-${order.id}`}
                    order={order}
                    isDuplicate
                  />
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Box>
      <Container maxW="container.xl" pb={24}>
        <Box textAlign="center" mt={10}>
          <a
            href="/comic-reading-order"
            className="button button-secondary button-wide-mobile latest-orders-cta"
            onClick={() =>
              trackEvent("latest_reading_orders_browse_click", {
                placement: "latest_reading_orders",
              })
            }
          >
            BROWSE ALL READING ORDERS
          </a>
        </Box>
      </Container>
    </section>
  );
};

export default LatestReadingOrders;
